import type {
  EventMetrics,
  EventFormData,
  MarketingEvent,
  AttendeeStatus,
} from '@/types/entities/marketing-event';
import type { PaginatedResponse } from '@/types/entities';

import { ApiService } from './api.service';
import { endpoints } from './endpoints';

interface GetEventsParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

interface CheckInPayload {
  email?: string;
  contactId?: string;
}

interface InvitationPayload {
  listId: string;
  templateId?: string;
  customHtml?: string;
  subject?: string;
}

interface InvitationResult {
  invited: number;
  skipped: number;
}

/**
 * Marketing events API service
 */
export class MarketingEventApiService extends ApiService {
  /**
   * Get all events with optional filters and pagination
   */
  getEvents = async (params?: GetEventsParams): Promise<PaginatedResponse<MarketingEvent>> =>
    this.get(endpoints.marketingEvents.filtered, { params });

  /**
   * Get a single event by ID
   */
  getEventById = async (id: string): Promise<MarketingEvent> =>
    this.get(endpoints.marketingEvents.byId(id));

  /**
   * Create a new marketing event
   */
  createEvent = async (data: EventFormData): Promise<{ id: string }> =>
    this.post(endpoints.marketingEvents.create, data);

  /**
   * Update an existing event
   */
  updateEvent = async (id: string, data: Partial<EventFormData>): Promise<MarketingEvent> =>
    this.patch(endpoints.marketingEvents.update(id), data);

  /**
   * Soft-delete an event
   */
  deleteEvent = async (id: string): Promise<void> =>
    this.delete(endpoints.marketingEvents.delete(id));

  /**
   * Get event metrics (conversion & attendance rates)
   */
  getMetrics = async (id: string): Promise<EventMetrics> =>
    this.get(endpoints.marketingEvents.metrics(id));

  /**
   * Check in an attendee by email or contactId
   */
  checkIn = async (
    eventId: string,
    payload: CheckInPayload,
  ): Promise<{ success: boolean }> =>
    this.patch(endpoints.marketingEvents.checkIn(eventId), payload);

  /**
   * Manually register a guest with 'registered' status
   */
  registerGuest = async (
    eventId: string,
    payload: CheckInPayload,
  ): Promise<{ success: boolean }> =>
    this.post(endpoints.marketingEvents.registerGuest(eventId), payload);

  /**
   * Update the attendance status of an existing attendee
   */
  updateAttendeeStatus = async (
    eventId: string,
    contactId: string,
    status: AttendeeStatus,
  ): Promise<{ success: boolean }> =>
    this.patch(endpoints.marketingEvents.updateAttendeeStatus(eventId), { contactId, status });

  /**
   * Send invitations from a marketing list
   */
  sendInvitations = async (
    eventId: string,
    payload: InvitationPayload,
  ): Promise<InvitationResult> =>
    this.post(endpoints.marketingEvents.invite(eventId), payload);
}

// Export a singleton instance
export const marketingEventApiService = new MarketingEventApiService();

// Named exports for convenience
export const {
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  getMetrics,
  checkIn,
  registerGuest,
  updateAttendeeStatus,
  sendInvitations,
} = marketingEventApiService;
